<script setup lang="ts">
import type Task from '@/core/resources/Task'
import GenericTable from '@/components/GenericTable.vue'
import TaskTableLine from '@/components/tables/TaskTableLine.vue'
import PaginationMenu from '@/components/tables/PaginationMenu.vue'
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { taskService } from '@/core/services'
import { useStore } from '@/core/store'
import { handleError } from '@/utils/ErrorHandler'
import { popupUtil } from '@/utils/Popup'

const fetchTasks = async (force?: boolean) => {
  return await taskService
    .getAllPaginated(
      store.getAccessToken(),
      pagination.value.itemsPerPage,
      pagination.value.page,
      sortParams.value.key,
      sortParams.value.desc,
      search.value
    )
    .then((res) => {
      if (selectedTasks().length == 0 || force) {
        taskList.value = res.results
        pagination.value.itemsLength = res.count
        pagination.value.pageCount = res.num_pages
        pagination.value.pageStart = res.start_index - 1
        pagination.value.pageStop = res.end_index

        selectedMap.value = {}
        res.results.forEach((t) => {
          selectedMap.value[t.id] = false
        })
        allSelected.value = false
      }

      return true
    })
    .catch((err) => {
      handleError(err)
      return false
    })
}

const deleteTasks = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to delete the selected tasks?')
  if (!confirmed) return

  taskService
    .deleteMany(selectedTasks(), store.getAccessToken())
    .then(() => {
      fetchTasks(true)
    })
    .catch(handleError)
}

const restartTasks = async () => {
  let confirmed = await popupUtil.confirm('Are you sure you want to restart the selected tasks?')
  if (!confirmed) return

  taskService
    .restart(selectedTasks(), store.getAccessToken())
    .then(() => {
      fetchTasks(true)
    })
    .catch(handleError)
}

const selectedTasks = () => {
  return taskList.value.filter((t) => selectedMap.value[t.id])
}

const handleSearch = () => {
  pagination.value.page = 1
  fetchTasks(true)
}

let interval: any
const allSelected = ref(false)
const store = useStore()
const taskList = ref<Task[]>([])
const selectedMap = ref<{ [key: number]: boolean }>({})
const search = ref('')
const sortParams = ref({ key: '', desc: false })
const headers = [
  { key: 'failed_at', label: 'Status' },
  { key: 'verbose_name', label: 'Name' },
  { key: 'run_at', label: 'Run at' },
  { key: 'creator_object_id', label: 'Owner' }
]
const pagination = ref({
  itemsLength: 0,
  itemsPerPage: 100,
  pageCount: 0,
  page: 1,
  pageStart: 0,
  pageStop: 0
})

const intervalFunction = async () => {
  if (selectedTasks().length != 0) {
    interval = setTimeout(intervalFunction, 1000)
  } else {
    const success = await fetchTasks()
    if (success) interval = setTimeout(intervalFunction, 1000)
  }
}

onMounted(() => {
  intervalFunction()
})

onBeforeUnmount(() => {
  clearInterval(interval)
})

watch(allSelected, (value) => {
  for (const key in selectedMap.value) {
    selectedMap.value[key] = value
  }
})
</script>

<template>
  <div class="container">
    <h1>List of tasks</h1>
    <div class="toolbar">
      <button @click="fetchTasks(true)" id="fetch-tasks">
        <span class="material-symbols-outlined">refresh</span>
        <span>Refresh</span>
      </button>
      <button :disabled="selectedTasks().length === 0" @click="deleteTasks" id="delete-tasks">
        <span class="material-symbols-outlined">delete</span>
        <span>Delete</span>
      </button>

      <button :disabled="selectedTasks().length === 0" @click="restartTasks" id="restart-tasks">
        <span class="material-symbols-outlined">replay</span>
        <span>Restart</span>
      </button>

      <div class="search-bar">
        <span class="material-symbols-outlined search-icon">search</span>
        <input type="text" v-model="search" placeholder="Search..." @keyup="handleSearch" />
      </div>
    </div>

    <GenericTable :headers="headers" v-model:sortParams="sortParams" v-model:checked="allSelected" @fetch="fetchTasks">
      <TaskTableLine v-for="task in taskList" :key="task.id" :task="task" v-model="selectedMap[task.id]" />
    </GenericTable>

    <PaginationMenu
      v-model:page="pagination.page"
      v-model:items-per-page="pagination.itemsPerPage"
      :pagination="pagination"
      @fetch="fetchTasks"
      :optionValues="[10, 50, 100, 200, 500, 1000, 5000]" />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/table.scss';
</style>
